import {
  ClipboardListIcon,
  LockKeyholeIcon,
  SearchCheckIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  CandidateLedgerAssessment,
  CandidatePrescreenContext,
  LedgerRevisionComparisonNotice,
} from '@/modules/candidates/components/CandidateLedgerAssessment';
import { useCandidateAssessmentQuery } from '@/modules/candidates/queries/useCandidateAssessmentQuery';
import type { CandidateAssessmentDataApi } from '@/modules/candidates/types';
import { prescreenContextFromAssessment } from '@/modules/candidates/utils/candidateAssessment';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Badge } from '@/modules/ui/components/badge/Badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import Loader from '@/modules/ui/components/loader/Loader';

interface CandidateAssessmentPanelProps {
  jobId: string;
  candidateId: string;
}

type AssessmentSnapshot = NonNullable<
  CandidateAssessmentDataApi['sourcedAssessment']
>;
type AssessmentFinding = AssessmentSnapshot['strengths'][number];

function formatScore(score: number | null) {
  return score === null ? 'Not available' : `${score}/100`;
}

function FindingList({
  title,
  findings,
  tone = 'neutral',
}: {
  title: string;
  findings: AssessmentFinding[];
  tone?: 'positive' | 'warning' | 'neutral';
}) {
  if (findings.length === 0) return null;

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-neutral-900">{title}</h4>
      <div className="space-y-2">
        {findings.map((finding, index) => (
          <div
            key={`${finding.summary}-${index}`}
            className={cn(
              'rounded-lg border p-3',
              tone === 'positive' &&
                'border-green-200 bg-green-50 text-green-950',
              tone === 'warning' &&
                'border-amber-200 bg-amber-50 text-amber-950',
              tone === 'neutral' && 'border-neutral-200 bg-neutral-50',
            )}
          >
            <p className="text-sm font-medium">{finding.summary}</p>
            <p className="mt-1 text-sm leading-5 opacity-80">
              {finding.explanation}
            </p>
            {finding.evidenceSources.length > 0 && (
              <p className="mt-2 text-xs opacity-70">
                Evidence: {finding.evidenceSources.join(', ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function SnapshotFindings({ snapshot }: { snapshot: AssessmentSnapshot }) {
  return (
    <div className="space-y-4">
      <FindingList
        title="Strengths"
        findings={snapshot.strengths}
        tone="positive"
      />
      <FindingList
        title="Concerns"
        findings={snapshot.concerns}
        tone="warning"
      />
      <FindingList title="Open questions" findings={snapshot.openQuestions} />
    </div>
  );
}

function SourceAssessment({
  snapshot,
  score,
}: {
  snapshot: CandidateAssessmentDataApi['sourcedAssessment'];
  score: number | null;
}) {
  return (
    <section className="rounded-xl border border-blue-200 bg-blue-50/40 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
            <SearchCheckIcon className="size-5" aria-hidden />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-neutral-950">
                Source assessment
              </h3>
              <Badge variant="outline" size="sm">
                <LockKeyholeIcon /> Immutable
              </Badge>
            </div>
            <p className="text-neutral-650 mt-1 text-sm">
              Profile evidence captured before the pre-screen.
            </p>
          </div>
        </div>
        <Score value={score} label="Source match" />
      </div>

      {snapshot ? (
        <div className="mt-5 space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-neutral-900">
              Source summary
            </h4>
            <p className="mt-1 text-sm leading-6 text-neutral-700">
              {snapshot.summary}
            </p>
          </div>
          <SnapshotFindings snapshot={snapshot} />
        </div>
      ) : (
        <p className="text-neutral-650 mt-5 text-sm">
          No immutable source snapshot was captured for this legacy candidate.
        </p>
      )}
    </section>
  );
}

function PostPrescreenAssessment({
  snapshot,
  score,
  agentDecision,
  suggestion,
}: {
  snapshot: CandidateAssessmentDataApi['scoredAssessment'];
  score: number | null;
  agentDecision: CandidateAssessmentDataApi['agentDecision'];
  suggestion: CandidateAssessmentDataApi['suggestion'];
}) {
  return (
    <section className="rounded-xl border border-violet-200 bg-violet-50/40 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
            <ClipboardListIcon className="size-5" aria-hidden />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-neutral-950">
                Post-prescreen assessment
              </h3>
              <Badge variant="outline" size="sm">
                <LockKeyholeIcon /> Immutable
              </Badge>
            </div>
            <p className="text-neutral-650 mt-1 text-sm">
              Prescreen evidence, AI decision, and AI suggestion.
            </p>
          </div>
        </div>
        <Score value={score} label="Assessment" />
      </div>

      {snapshot ? (
        <div className="mt-5 space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-neutral-900">
              Assessment summary
            </h4>
            <p className="mt-1 text-sm leading-6 text-neutral-700">
              {snapshot.summary}
            </p>
          </div>

          {snapshot.prescreen && (
            <div className="rounded-lg border border-violet-100 bg-white/70 p-4">
              <h4 className="text-sm font-semibold text-neutral-900">
                Prescreen evidence
              </h4>
              <p className="mt-1 text-sm leading-6 text-neutral-700">
                {snapshot.prescreen.summary}
              </p>
              <div className="mt-4">
                <FindingList
                  title="Confirmed facts"
                  findings={snapshot.prescreen.confirmedFacts}
                  tone="positive"
                />
              </div>
            </div>
          )}

          <SnapshotFindings snapshot={snapshot} />
        </div>
      ) : (
        <p className="text-neutral-650 mt-5 text-sm">
          No immutable post-prescreen snapshot was captured for this legacy
          candidate.
        </p>
      )}

      {agentDecision && (
        <div className="mt-5 rounded-lg border border-violet-200 bg-white/80 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-neutral-900">
              AI decision
            </h4>
            <Badge variant="outline" size="sm">
              {agentDecision.outcome}
            </Badge>
          </div>
          <p className="mt-2 text-sm leading-6 text-neutral-700">
            {agentDecision.reasoning}
          </p>
        </div>
      )}

      {suggestion && (
        <div className="mt-5 rounded-lg border border-violet-200 bg-white/80 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-neutral-900">
              AI suggestion
            </h4>
            <Badge variant="outline" size="sm">
              {suggestion.outcome}
            </Badge>
          </div>
          <p className="mt-2 text-sm leading-6 text-neutral-700">
            {suggestion.reasoning}
          </p>
        </div>
      )}
    </section>
  );
}

function Score({ value, label }: { value: number | null; label: string }) {
  return (
    <div className="rounded-lg border bg-white px-3 py-2 text-right shadow-xs">
      <p className="text-muted-foreground text-xs">{label} score</p>
      <p className="text-sm font-semibold text-neutral-950">
        {formatScore(value)}
      </p>
    </div>
  );
}

export function CandidateAssessmentPanel({
  jobId,
  candidateId,
}: CandidateAssessmentPanelProps) {
  const assessmentQuery = useCandidateAssessmentQuery({ jobId, candidateId });

  if (assessmentQuery.isLoading) {
    return (
      <Card>
        <CardContent>
          <Loader />
        </CardContent>
      </Card>
    );
  }

  if (assessmentQuery.isError || !assessmentQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Assessment could not be loaded. Refresh the page or try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const response = assessmentQuery.data;
  const assessment = response.assessment;

  if (assessment.assessmentMode === 'ledger' && assessment.ledger) {
    const comparisonAssessment =
      assessment.ledger.atSubmission?.stage === 'prescreen'
        ? assessment.ledger.scoredAssessment
        : assessment.ledger.sourcedAssessment;
    const showSubmissionRevision =
      assessment.ledger.atSubmission &&
      assessment.ledger.atSubmission.revisionId !==
        comparisonAssessment.revisionId;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Candidate assessment</CardTitle>
          <CardDescription>
            Requirement-level evidence, coverage, and the question sets saved
            for pre-screening.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <CandidateLedgerAssessment
            assessment={assessment.ledger.sourcedAssessment}
            title="Source assessment"
            description="Profile evidence captured before the pre-screen."
          />
          <CandidateLedgerAssessment
            assessment={assessment.ledger.scoredAssessment}
            title="Post-prescreen assessment"
            description="The same requirements re-evaluated with call evidence."
          />
          {showSubmissionRevision && assessment.ledger.atSubmission && (
            <>
              <LedgerRevisionComparisonNotice
                current={comparisonAssessment}
                atSubmission={assessment.ledger.atSubmission.assessment}
              />
              <CandidateLedgerAssessment
                assessment={assessment.ledger.atSubmission.assessment}
                title="Assessment shown at submission"
                description="The immutable assessment revision used when this candidate was submitted."
                compact
                showAvailabilityBadge={false}
              />
            </>
          )}
          <CandidatePrescreenContext
            context={prescreenContextFromAssessment(response)}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Candidate assessment</CardTitle>
        <CardDescription>
          Immutable source and post-prescreen assessment evidence.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-5 xl:grid-cols-2">
          <SourceAssessment
            snapshot={assessment.sourcedAssessment}
            score={assessment.sourceMatchScore}
          />
          <PostPrescreenAssessment
            snapshot={assessment.scoredAssessment}
            score={assessment.assessmentScore}
            agentDecision={assessment.agentDecision}
            suggestion={assessment.suggestion}
          />
        </div>
      </CardContent>
    </Card>
  );
}
